import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextInput from "../../../../../lib/html5-software-library/src/ranj/components/text/TextInput";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import PlayerModel from "../../player/model/PlayerModel";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import PromiseUtil from "../../util/PromiseUtil";
import Slice9Panel from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Panel";
import MovieClipUtil from "../../util/MovieClipUtil";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import SoundType from "../../../core/sound/util/SoundType";

export default class PlayerNameInputScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {AssetLibrary} assetLibrary
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, assetLibrary, playerModel, languagePack, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);
        this._assetLibrary = assetLibrary;
        this._playerModel = playerModel;
        this._languagePack = languagePack;
        this._soundManager = soundManager;

        this._currentTypeWriter = null;

        this._handleKeyPressedBound = this._handleKeyPressed.bind(this);
    }

    async initialize() {
        super.initialize();

        this._panel = this._target.panel;
        this._coach = this._target.coach;
        this._coach.initialX = this._coach.x;
        this._nameInputPanel = this._target.nameInputPanel;

        this._slicedPanel = new Slice9Panel(this._panel, MovieClipUtil.getFirstBitmapChild(this._panel), {
            mode: Slice9Panel.VERTICAL,
            pixelPerfect: true,
            overlap: false,
        });
        this._panel.coachTextField.text = this._languagePack.getText(`coachIntroduction.askPlayerName`)?.trim();
        this._slicedPanel.update();

        window.addEventListener('keypress', this._handleKeyPressedBound);

        const inputFieldBounds = this._nameInputPanel.inputText.getBounds();
        this._textInput = new TextInput(this._nameInputPanel.inputText, inputFieldBounds.width, inputFieldBounds.height, 'gameContainer', 50);
        this._textInput.setTextChangedCallback(this._handleTextChanged.bind(this));

        this._nameInputPanel.inputText.text = "";
        this._textInput.setText("");
        this._textInput.setDefaultText(this._languagePack.getText(`playerNameInput.inputPlaceholder`));
        this._nameInputPanel.addEventListener('click', () => (this._textInput.focus()));

        this._sendButton = this.createButton(this._nameInputPanel.sendButton, this._handleSendButton.bind(this), this._languagePack.getText(`playerNameInput.confirmButtonLabel`), SoundType.BUTTON_CLICK);
        this._sendButton.setEnabled(false);

        this._panel.coachTextField.text = "";

        await this._animateIn(650); // with a delay to allow the screen to transition in first.

        await this._typeWriteText(this._languagePack.getText(`coachIntroduction.askPlayerName`)?.trim());
        await PromiseUtil.delay(1050);
        await this._showPlayerInputPanel();
    }

    destroy() {
        super.destroy();

        this._textInput.destroy();

        window.removeEventListener('keypress', this._handleKeyPressedBound);
    }

    async _typeWriteText(text) {
        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        await new Promise(resolve => {
            this._panel.coachTextField.text = text;
            this._slicedPanel.update();

            this._panel.coachTextField.text = "";

            const duration = text.length * .007;
            this._currentTypeWriter?.stop();
            this._currentTypeWriter = TextTypeWriterAnimation.animate(this._panel.coachTextField, text, duration, 0, resolve);
            this._slicedPanel.update();
        });
        this._soundManager.stopSound(typewriteSoundId, 50);
    }

    async _handleSendButton() {
        const currentText = this._textInput.getText().trim();
        if (currentText.length === 0) {
            return;
        }

        this._playerModel.playerName = currentText;

        await this._animateOut();

        this._soundManager.stop(); // Stop all sounds (e.g., typewriter sound).

        this.openScreen(SkinKeyRegistry.COACH_INTRODUCTION_SCREEN_SKIN);
        this.closeScreen(this._target);
    }

    // Disable the send button when the input field is empty
    _handleTextChanged() {
        const currentText = this._textInput.getText();
        this._textInput.setText(currentText);
        if (currentText?.trim().length === 0) {
            this._sendButton.setEnabled(false);
        } else {
            this._sendButton.setEnabled(true);
        }
    }

    _handleKeyPressed(evt) {
        // If the enter key is pressed
        if (evt.charCode === 13) {
            this._handleSendButton();
        }
    }

    async _animateIn(delay = 0) {
        this._coach.alpha = 0;

        this._panel.alpha = 0;
        this._panel.scale = .8;

        this._nameInputPanel.alpha = 0;
        this._nameInputPanel.scale = .8;

        await PromiseUtil.delay(delay);

        this._soundManager.playSound(SoundType.COACH_SHOW);
        this._coach.gotoAndPlay(0);
        await createjs.Tween.get(this._coach)
            .to({
                alpha: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();

        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_SHOW);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 1,
                scale: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }

    async _animateOut() {
        await createjs.Tween.get(this._nameInputPanel)
            .to({
                alpha: 0,
                scale: .8,
            }, 450, createjs.Ease.backIn)
            .toPromise();

        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_HIDE);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 0,
                scale: .8,
            }, 450, createjs.Ease.backIn)
            .toPromise();
    }

    async _showPlayerInputPanel() {
        this._soundManager.playSound(SoundType.PLAYER_INPUT_PANEL_APPEARS);
        await createjs.Tween.get(this._nameInputPanel)
            .to({
                alpha: 1,
                scale: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }
}
